import { AppSidebar } from "@/components/app-sidebar";
import { NavUser } from "@/components/header/nav-user";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";

import BackgroundRunningTask from "@/components/background-running-tasks";
import GlobalModals from "@/components/global-modals";
import OnboardingWizardDialog from "@/components/onboarding-wizard.tsx";
import BasicTourGuide from "@/components/tour-guide/basics";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTourProgress } from "@/hooks/use-organisation";
import { useGetUsage } from "@/hooks/use-subscription";
import { ProtectedWrapper } from "@/providers/protected-wrapper";
import { Heartbeat } from "@phosphor-icons/react";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { Inbox } from "lucide-react";
import React from "react";
import ThemeWrapper from "@/providers/theme-wrapper";

export const Route = createFileRoute("/_private")({
  component: ProtectedLayout,
  beforeLoad: ({ context, location }) => {
    if (!context.auth?.loading && !context.auth?.session.active_signin) {
      // console.log("redirecting to signin from private layout beforeLoad");
      throw redirect({
        to: "/signin",
        replace: true,
        search: {
          redirect: location.href,
        },
      });
    }
  },
});

function BatteryIcon({ level }: { level: number }) {
  // Calculate fill percentage based on credit level
  const getFillPercentage = () => {
    if (level <= 1000) return 25;
    if (level <= 2000) return 50;
    if (level <= 3000) return 75;
    return 100;
  };

  // Get color based on credit level
  const getColor = () => {
    if (level <= 1000) return "#ef4444"; // red
    if (level <= 2000) return "#eab308"; // yellow
    if (level <= 3000) return "#22c55e"; // green
    return "#22c55e"; // green
  };

  const fillPercentage = getFillPercentage();
  const color = getColor();

  return (
    <div className="relative h-5 w-8">
      <svg
        viewBox="0 0 32 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full"
      >
        {/* Battery outline */}
        <rect
          x="1"
          y="1"
          width="28"
          height="14"
          rx="2"
          stroke="currentColor"
          strokeWidth="2"
          className="text-gray-400"
        />
        {/* Battery tip */}
        <rect x="30" y="5" width="2" height="6" className="fill-gray-400" />
        {/* Battery fill */}
        <rect
          x="3"
          y="3"
          width={`${(24 * fillPercentage) / 100}`}
          height="10"
          rx="1"
          fill={color}
        />
      </svg>
    </div>
  );
}

function ProtectedLayout() {
  return (
    <ProtectedWrapper>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex sticky top-0 bg-background h-16 shrink-0 items-center justify-between gap-2 border-b px-4 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="h-4" />
              <ActiveBreadcrumb />
            </div>
            <div className="flex items-center gap-4">
              {/* <EmailHealthIndicator /> */}
              {/* <InboxPlacementIndicator /> */}
              {/* <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">
                      Available Inbox Placement Tests:
                    </div>
                    <div className="text-sm text-muted-foreground">56 / 73</div>
                  </div> */}
              <Separator orientation="vertical" className="h-4" />
              <NavUser />
            </div>
          </header>
          {/* <BackgroundRunningTask /> */}

          <GlobalModals />

          {/* 7 questions */}

          {/* free banner */}

          {/* tour guide */}

          <TourGuideWrapper />

          <div className="flex h-full p-4 md:p-6 max-w-[1600px] w-full mx-auto">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </ProtectedWrapper>
  );
}

const ActiveBreadcrumb = () => {
  const state = useRouterState();
  const pathnames = state.location.pathname.split("/").filter((x) => x);

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/">Home</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        {pathnames.map((pathname, index) => {
          // Build cumulative path for each breadcrumb
          const path = "/" + pathnames.slice(0, index + 1).join("/");

          return (
            <React.Fragment key={pathname}>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={path} className="capitalize">
                    {decodeURIComponent(pathname)}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="last:hidden" />
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

const SubscriptionIndicator = () => {
  const { data: usage } = useGetUsage();
  const credits = usage?.credits || 0;

  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="flex items-center gap-2">
          <BatteryIcon level={credits} />
          <span className="font-medium">{credits}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{credits} credits left</p>
      </TooltipContent>
    </Tooltip>
  );
};

const EmailHealthIndicator = () => {
  // const { data: usage } = useGetUsage();
  // const credits = usage?.credits || 0;

  const credits = 73;

  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="flex flex-col items-center rounded-md p-1 px-2">
          <div className="flex items-center gap-1">
            <div className="size-7 rounded-md bg-muted text-foreground border flex items-center justify-center">
              <Heartbeat className="w-4 h-4" />
            </div>

            <span className=" text-sm font-medium italic">{credits}</span>
          </div>
          {/* <span className="text-xs text-muted-foreground">
            Email Health Tests
          </span> */}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{credits} credits left</p>
      </TooltipContent>
    </Tooltip>
  );
};

const InboxPlacementIndicator = () => {
  // const { data: usage } = useGetUsage();
  // const credits = usage?.credits || 0;

  const credits = 51;

  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="flex flex-col items-center rounded-md p-1 px-2">
          <div className="flex items-center gap-2">
            <div className="size-7 rounded-md bg-muted text-foreground border flex items-center justify-center">
              <Inbox className="w-4 h-4" />
            </div>
            <div className="flex flex-col items-start -space-y-1">
              <span className=" text-sm font-medium italic">{credits}</span>
              <span className="text-[10px] text-muted-foreground">
                Placement Tests
              </span>
            </div>
          </div>
          {/* <span className="text-xs text-muted-foreground">
            Email Health Tests
          </span> */}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{credits} credits left</p>
      </TooltipContent>
    </Tooltip>
  );
};

const TourGuideWrapper = () => {
  const { data: tourProgress, isLoading, error } = useTourProgress();
  // console.log(tourProgress, "error");
  if (isLoading && !error) {
    return <></>;
  } else {
    // Don't show tour if it's dismissed
    if (tourProgress?.isTourDismissed) {
      return <></>;
    }
    if (!tourProgress?.isAnsweredAllQuestions && !isLoading) {
      return <OnboardingWizardDialog />;
    } else if (
      (tourProgress?.isAnsweredAllQuestions && !isLoading) ||
      tourProgress?.isSeenWelcomePopup
    ) {
      return <BasicTourGuide />;
    } else {
      return <></>;
    }
  }
};
